import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

interface AuthContextType {
  isAdmin: boolean;
  loading: boolean;
  login: (password: string) => Promise<{ ok: boolean; error?: string }>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType>({
  isAdmin: false,
  loading: true,
  login: async () => ({ ok: false }),
  logout: () => {},
});

const TOKEN_KEY = 'riddim-admin-token';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  // Check existing token on mount
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setLoading(false);
      return;
    }
    fetch('/api/auth/check', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(data => {
        setIsAdmin(data.authenticated === true);
        if (!data.authenticated) localStorage.removeItem(TOKEN_KEY);
      })
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setIsAdmin(false);
      })
      .finally(() => setLoading(false));
  }, []);

  // Listen for token expiry from API calls (401 responses)
  useEffect(() => {
    function handleExpired() {
      setIsAdmin(false);
      console.log('[AUTH] Token expired — logged out');
    }
    window.addEventListener('auth-expired', handleExpired);
    return () => window.removeEventListener('auth-expired', handleExpired);
  }, []);

  const login = useCallback(async (password: string) => {
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      const data = await res.json();
      if (res.ok && data.token) {
        localStorage.setItem(TOKEN_KEY, data.token);
        setIsAdmin(true);
        return { ok: true };
      }
      return { ok: false, error: data.error || 'Login failed' };
    } catch (err: any) {
      return { ok: false, error: err.message };
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setIsAdmin(false);
  }, []);

  return (
    <AuthContext.Provider value={{ isAdmin, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

/** Get the stored token (for use in API service) */
export function getAuthToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
